import { sessionStart } from './dateUtils';

// Calendar export for a study session: Google Calendar link and .ics file
// (website), or the iPhone's own Add to Calendar sheet (app).
// Used by Sessions and by the sheet shown right after accepting a request.

const TWO_HOURS = 2 * 60 * 60 * 1000;

// Video rooms need a fresh token from the app, so events link to MedXMeet with the
// session id; opening that link starts the session's video call (Dashboard).
export const APP_LINK = 'https://medxmeet.com';
export const joinLink = (sessionId) => (sessionId ? `${APP_LINK}/?join=${encodeURIComponent(sessionId)}` : APP_LINK);

// UTC timestamps (…Z) so the event lands at the right instant in every calendar app.
const fmt = d => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';

// Start, end and app link, or null when the session has no readable date.
const eventTimes = (session) => {
  const start = sessionStart(session);
  if (!start) return null;
  return { start, end: new Date(start.getTime() + TWO_HOURS), link: joinLink(session.id) };
};

export const googleCalendarUrl = (session, partner) => {
  const ev = eventTimes(session);
  if (!ev) return null;
  const details = `Topics: ${(session.subjects || []).join(', ')}\n\nJoin the video call:\n${ev.link}\n\nTip: set this event to repeat weekly if it's a standing session.`;
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent('Study Session with ' + partner)}&dates=${fmt(ev.start)}/${fmt(ev.end)}&details=${encodeURIComponent(details)}&location=${encodeURIComponent(ev.link)}`;
};

export const icsText = (session, partner) => {
  const ev = eventTimes(session);
  if (!ev) return null;
  const desc = `Topics: ${(session.subjects || []).join(', ')}\\n\\nJoin the video call: ${ev.link}`;
  return `BEGIN:VCALENDAR\r\nVERSION:2.0\r\nPRODID:-//MedXMeet//EN\r\nBEGIN:VEVENT\r\nUID:${session.id}@medxmeet.com\r\nDTSTART:${fmt(ev.start)}\r\nDTEND:${fmt(ev.end)}\r\nSUMMARY:Study Session with ${partner}\r\nDESCRIPTION:${desc}\r\nLOCATION:${ev.link}\r\nURL:${ev.link}\r\nEND:VEVENT\r\nEND:VCALENDAR`;
};

export const icsFileName = (partner) => `session-${String(partner || 'partner').replace(/\s+/g, '-')}.ics`;

// A blob: URL for the .ics, for a link the user taps (href + download).
export const icsObjectUrl = (session, partner) => {
  const text = icsText(session, partner);
  return text ? URL.createObjectURL(new Blob([text], { type: 'text/calendar' })) : null;
};

// iPhone app: the system's event editor, filled in; the user taps Add. The plugin
// loads only here, so the website never downloads it.
export const addToDeviceCalendar = async (session, partner) => {
  const ev = eventTimes(session);
  if (!ev) return false;
  const { CapacitorCalendar } = await import('@ebarooni/capacitor-calendar');
  await CapacitorCalendar.createEventWithPrompt({
    title: `Study Session with ${partner}`,
    startDate: ev.start.getTime(),
    endDate: ev.end.getTime(),
    location: ev.link,
    url: ev.link,
    description: `Topics: ${(session.subjects || []).join(', ')}\n\nJoin the video call:\n${ev.link}`,
  });
  return true;
};
